import { type DateValue, getLocalTimeZone } from "@internationalized/date";
import { ArrowDown, ArrowUp } from "@untitledui/icons";
import { MetricsSimple } from "@/components/application/metrics/metrics";
import { Badge } from "@/components/base/badges/badges";
import { Tooltip, TooltipTrigger } from "@/components/base/tooltip/tooltip";
import type { KPIData } from "@/hooks/use-dashboard-data";

export const getPeriodTooltip = (range: { start: DateValue; end: DateValue } | null) => {
  if (!range?.start || !range?.end) return "Change since last period";
  const diffTime = Math.abs(
    range.start.toDate(getLocalTimeZone()).getTime() - range.end.toDate(getLocalTimeZone()).getTime(),
  );
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
  // TODO: We no longer allow arbitrary date ranges, so the second option is the only available one
  if (diffDays < 7) return "Change since same period in last week";
  if (diffDays < 31) return "Change since same week in the previous month";
  return "Change since same period in last quarter";
};

export const getTimePeriodTooltip = (timePeriod: "7d" | "30d" | "all_time") => {
  switch (timePeriod) {
    case "7d":
      return "Change since same period in last week";
    case "30d":
      return "Change since same period in last month";
    case "all_time":
      return "Change since last period";
  }
};

export const Metrics = ({ data }: { data?: KPIData }) => (
  <MetricsSimple
    className="flex-1"
    title={data?.value}
    trend={data?.trend === "up" ? "positive" : "negative"}
    type="modern"
    footer={null}
    subtitle={data?.description}
    change={data?.change ? `${data.change} vs last week` : undefined}
  />
);

const percentFormatter = new Intl.NumberFormat("en-US", {
  style: "percent",
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
});

export const TrendBadge = ({
  change,
  dateRange,
  timePeriod,
}: {
  change: number | null;
  dateRange?: { start: DateValue; end: DateValue } | null;
  timePeriod?: "7d" | "30d" | "all_time";
}) => {
  if (change === null)
    return (
      <Tooltip title="No trend data">
        <TooltipTrigger className="inline">
          <Badge size="sm" color="gray" className=" scale-90 text-xs">
            N/A
          </Badge>
        </TooltipTrigger>
      </Tooltip>
    );
  const tooltip = timePeriod ? getTimePeriodTooltip(timePeriod) : getPeriodTooltip(dateRange ?? null);
  return (
    <Tooltip title={tooltip}>
      <TooltipTrigger>
        <Badge size="sm" color={change >= 0 ? "success" : "error"} className=" scale-90 text-xs flex">
          {change >= 0 ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />}
          {percentFormatter.format(change)}
        </Badge>
      </TooltipTrigger>
    </Tooltip>
  );
};
